import { normalizeSearch } from "/app/apps/web/src/routes/_app/search/index.tsx?tsr-shared=1";
const $$splitComponentImporter = () => import('/app/apps/web/src/routes/_app/search/index.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
const $$splitLoaderImporter = () => import('/app/apps/web/src/routes/_app/search/index.tsx?tsr-split=loader');
import { lazyFn } from '@tanstack/react-router';
import { createFileRoute } from "@tanstack/react-router";
import { createTranslator } from "use-intl";
import { alternatesToLinks, buildCanonicalAlternates } from "@/i18n/alternates";
import { buildPagePreviewMetaTags } from "@/lib/buildPagePreviewMetaTags";
import { getRootLoaderData } from "@/types/intl";
interface SearchSearch {
  q?: string;
  genre?: string;
  style?: string;
  type?: AlbumType;
  format?: FormatCategory;
  country?: string;
  origin?: string;
  year?: number[];
  page?: number;
  label?: string[];
  company?: string[];
  forSale?: boolean;
  sort?: string;
  cursor?: string;
}
export const Route = createFileRoute("/_app/search/")({
  validateSearch: normalizeSearch,
  // Search/filter params are handled by React Query inside the page. Keeping
  // them out of loaderDeps prevents each keystroke from creating a new route
  // match and replacing the mobile page with TanStack Router's pending state.
  //
  // The loader still reads `location.search` on route entry so direct
  // `/search?q=...` loads and home-page submissions keep SSR-prefetched result
  // data. Later in-page URL edits stay client-query driven.
  loaderDeps: () => ({}),
  loader: lazyFn($$splitLoaderImporter, 'loader'),
  head: async ({
    match,
    loaderData
  }) => {
    const {
      locale,
      messages
    } = getRootLoaderData(match);
    const search = match.search as SearchSearch;
    const translators = {
      seo: createTranslator({
        locale,
        messages,
        namespace: "globals.seo"
      }),
      search: createTranslator({
        locale,
        messages,
        namespace: "globals.search"
      }),
      albumType: createTranslator({
        locale,
        messages,
        namespace: "globals.search.albumType"
      }),
      formats: createTranslator({
        locale,
        messages,
        namespace: "shared.formats"
      }),
      genres: createTranslator({
        locale,
        messages,
        namespace: "shared.genres"
      }),
      styles: createTranslator({
        locale,
        messages,
        namespace: "shared.styles"
      })
    };
    const {
      buildSearchCanonicalPath,
      buildSearchFilterPhrase,
      buildSearchSeoTitle
    } = await import("@/lib/seo");
    const canonicalPath = buildSearchCanonicalPath(search);
    const indexable = canonicalPath === "/search" && loaderData?.searchIndexableRoot === true;
    const alts = buildCanonicalAlternates(canonicalPath);
    const phrase = buildSearchFilterPhrase(search, translators, locale);
    const query = search.q?.trim();
    const title = buildSearchSeoTitle(search, translators, locale);
    const description = await searchRouteDescription(indexable, phrase, query, translators.seo);
    const jsonLd = indexable && !query ? (await import("@/utils/jsonld")).buildCollectionPageJsonLd({
      name: title,
      description,
      url: canonicalPath,
      pageType: canonicalPath === "/search" ? "CollectionPage" : "SearchResultsPage"
    }) : null;
    return {
      meta: await buildPagePreviewMetaTags({
        title,
        description,
        canonicalUrl: alts.canonical
      }, {
        robots: indexable ? null : "noindex,follow",
        openGraph: {},
        twitter: {}
      }, {
        locale
      }),
      links: indexable ? alternatesToLinks(alts) : [{
        rel: "canonical",
        href: alts.canonical
      }],
      scripts: jsonLd ? [{
        type: "application/ld+json",
        children: JSON.stringify(jsonLd)
      }] : undefined
    };
  },
  component: lazyRouteComponent($$splitComponentImporter, 'component')
});
async function searchRouteDescription(indexable: boolean, phrase: string | null, query: string | undefined, translateSeo: ReturnType<typeof createTranslator>) {
  const {
    buildSeoDescription
  } = await import("@/lib/seo");
  if (indexable && phrase) {
    return buildSeoDescription([translateSeo("searchBrowseDescription", {
      phrase
    })]);
  }
  if (query) return buildSeoDescription([translateSeo("searchQueryDescription", {
    query
  })]);
  return buildSeoDescription([translateSeo("searchDescription")]);
}